import React, { useEffect, useMemo, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import { Link, useNavigate } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import { batchList } from "../../api/Batch";
import { courseList } from "../../api/Courses";
import Master from "./Master";
import { handleStudentDownload } from "./components/Master";
import Pagination from "../components/Pagination";
import { formatToDate } from "../master/FormatToDateTime";
import { duplicateStudentsList } from "../../api/Student";

const FILTER_STORAGE_KEY = "students_filters";
const DEFAULT_FILTERS = {
    duplicate_by: ["phone"], // array — multi-select
    sort_by: "name",
    sort_order: "asc",
    page: 1,
};

const DUPLICATE_FIELD_MAP = {
    phone: "phone",
    secondary_mobile: "secondary_mobile",
    father_mobile: "father_mobile",
    mother_mobile: "mother_mobile",
    name: "name",
    father_name: "father_name",
    mother_name: "mother_name",
    registration_no: "registration_no",
    dob: "dob",
    aadhar_no: "aadhar_no",
    jan_aadhar: "jan_aadhar",
};

const DUPLICATE_BY_LABELS = {
    phone: "Mobile",
    secondary_mobile: "Secondary Mobile",
    father_mobile: "Father Mobile",
    mother_mobile: "Mother Mobile",
    name: "Student Name",
    father_name: "Father Name",
    mother_name: "Mother Name",
    registration_no: "Registration No",
    dob: "Date Of Birth",
    aadhar_no: "Aadhar No",
    jan_aadhar: "Jan Aadhar",
};

export default function StudentsDuplicate() {
    const hasAccess = usePrivateLink();
    const navigate = useNavigate();
    const [loading, setLoading] = useState("");
    const [sessions, setSessions] = useState([]);
    const [courses, setCourses] = useState([]);
    const [batches, setBatches] = useState([]);

    const getSavedFilters = () => {
        try {
            const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.duplicate_by && !Array.isArray(parsed.duplicate_by)) {
                    parsed.duplicate_by = [parsed.duplicate_by];
                }
                return parsed;
            }
        } catch (e) { }
        return { ...DEFAULT_FILTERS, duplicate_by: [...DEFAULT_FILTERS.duplicate_by] };
    };

    const [filters, setFilters] = useState(getSavedFilters);
    const [tempFilters, setTempFilters] = useState(getSavedFilters);
    const [data, setData] = useState(null);

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const paramsObj = {
                ...filters,
                duplicate_by: Array.isArray(filters.duplicate_by)
                    ? filters.duplicate_by.join(",")
                    : filters.duplicate_by,
            };
            const params = new URLSearchParams(paramsObj).toString();
            const response = await duplicateStudentsList(params);
            if (response.data.status) {
                setData(response.data.data);
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ?? error?.message
            );
        } finally {
            setLoading(false);
        }
    };

    const getBatchList = async (courseId) => {
        setLoading(true);
        try {
            let addUrl = "";
            if (courseId) {
                addUrl = `course_id=${courseId}`;
            }
            const response = await batchList(addUrl);
            if (response.data.status) {
                setBatches(response?.data?.data);
            } else {
                SweetAlertShow("info", "Warning", response?.data?.message);
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ?? error?.response?.message ?? error?.message
            );
        } finally {
            setLoading(false);
        }
    };

    const courseFetch = async () => {
        setLoading(true);
        try {
            const response = await courseList();
            if (response.data.status) {
                setCourses(response?.data?.data || []);
            } else {
                SweetAlertShow("info", "Warning", response?.data?.message);
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ?? error?.response?.message ?? error?.message
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
        setSessions(parseSessions);
    }, []);

    useEffect(() => {
        const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
        if (!activeSession) return;

        setFilters((prev) => {
            if (prev.session_id) return prev;
            const updated = { ...prev, session_id: activeSession };
            sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });

        setTempFilters((prev) => {
            if (prev.session_id) return prev;
            return { ...prev, session_id: activeSession };
        });
    }, [sessions]);

    useEffect(() => {
        courseFetch();
    }, []);

    useEffect(() => {
        getBatchList(tempFilters?.course_id);
    }, [tempFilters?.course_id]);

    const handleOpenOffcanvas = () => {
        setTempFilters({ ...filters, duplicate_by: [...(filters.duplicate_by || [])] });
    };

    const handleApply = () => {
        const updated = {
            ...tempFilters,
            duplicate_by:
                Array.isArray(tempFilters.duplicate_by) && tempFilters.duplicate_by.length > 0
                    ? tempFilters.duplicate_by
                    : ["phone"],
            page: 1,
        };
        setFilters(updated);
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(updated));
    };

    const handleReset = () => {
        const reset = { ...DEFAULT_FILTERS, duplicate_by: [...DEFAULT_FILTERS.duplicate_by] };
        setTempFilters(reset);
        setFilters(reset);
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
    };

    const removeFilter = (key, extra) => {
        const updated = { ...filters };
        if (Array.isArray(key)) {
            key.forEach((k) => delete updated[k]);
        } else {
            updated[key] = "";
        }
        if (extra) {
            extra.forEach((k) => (updated[k] = ""));
        }
        setFilters(updated);
        setTempFilters(updated);
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(updated));
    };

    useEffect(() => {
        fetchStudents();
    }, [filters]);

    // ── Union-Find: jo students kisi bhi selected field pe match karte hain unhe ek group mein merge karo ──
    const unifiedGroups = useMemo(() => {
        const list = data?.data || [];
        const selectedFields =
            Array.isArray(filters?.duplicate_by) && filters.duplicate_by.length > 0
                ? filters.duplicate_by
                : ["phone"];

        if (list.length === 0) return [];

        const parent = new Map();
        const find = (x) => {
            while (parent.get(x) !== x) {
                parent.set(x, parent.get(parent.get(x)));
                x = parent.get(x);
            }
            return x;
        };
        const union = (a, b) => {
            const ra = find(a);
            const rb = find(b);
            if (ra !== rb) parent.set(ra, rb);
        };

        list.forEach((item) => parent.set(item.id, item.id));

        const matchedIds = new Set();

        selectedFields.forEach((fieldKey) => {
            const field = DUPLICATE_FIELD_MAP[fieldKey] || fieldKey;
            const valueMap = new Map();

            list.forEach((item) => {
                const key = (item?.[field] ?? "").toString().trim().toLowerCase();
                if (!key) return;
                if (!valueMap.has(key)) valueMap.set(key, []);
                valueMap.get(key).push(item);
            });

            valueMap.forEach((items) => {
                if (items.length < 2) return;
                items.forEach((it) => matchedIds.add(it.id));
                for (let i = 1; i < items.length; i++) {
                    union(items[0].id, items[i].id);
                }
            });
        });

        const rootMap = new Map();
        list.forEach((item) => {
            if (!matchedIds.has(item.id)) return;
            const root = find(item.id);
            if (!rootMap.has(root)) rootMap.set(root, []);
            rootMap.get(root).push(item);
        });

        return Array.from(rootMap.values()).filter((items) => items.length > 1).map((items) => ({ items }));
    }, [data, filters?.duplicate_by]);

    const totalDuplicateGroups = unifiedGroups.length;

    const selectedLabels = (filters.duplicate_by || [])
        .map((k) => DUPLICATE_BY_LABELS[k])
        .filter(Boolean)
        .join(", ");

    return (
        <>
            {loading && <LoadingDiv />}
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <Header />
                    <div className="layout-page">
                        <Nav />
                        <div className="content-wrapper">
                            <div className="container-fluid flex-grow-1 container-p-y">
                                <div className="row">
                                    <Master />
                                    <div className="col-12">
                                        <div className="card p-3 mb-3 shadow-sm border-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div
                                                        style={{
                                                            width: 45,
                                                            height: 45,
                                                            borderRadius: "12px",
                                                            background: "linear-gradient(45deg,#185fa5,#4f8cff)",
                                                            color: "#fff",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <i className="ri-user-3-line"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="m-0 fw-bold">Duplicate Student List</h5>
                                                        <small className="text-muted">
                                                            Find & manage duplicate student records
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {filters.session_id && (
                                                            <span className="badge rounded-pill bg-primary-subtle text-primary px-3 py-2">
                                                                Session:{" "}
                                                                {sessions.find((s) => s.id == filters.session_id)?.session}
                                                                <i
                                                                    className="ri-close-line ms-2 cursor-pointer"
                                                                    onClick={() => removeFilter("session_id")}
                                                                ></i>
                                                            </span>
                                                        )}

                                                        {filters.search && (
                                                            <span className="badge rounded-pill bg-dark-subtle text-dark px-3 py-2">
                                                                Search: {filters.search}
                                                                <i
                                                                    className="ri-close-line ms-2 cursor-pointer"
                                                                    onClick={() => removeFilter("search")}
                                                                ></i>
                                                            </span>
                                                        )}

                                                        {filters.course_id && (
                                                            <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
                                                                Course:{" "}
                                                                {courses.find((c) => c.id == filters.course_id)?.title}
                                                                <i
                                                                    className="ri-close-line ms-2 cursor-pointer"
                                                                    onClick={() => removeFilter("course_id")}
                                                                ></i>
                                                            </span>
                                                        )}
                                                        {filters.batch_id && (
                                                            <span className="badge rounded-pill bg-warning-subtle text-dark px-3 py-2">
                                                                Batch:{" "}
                                                                {batches.find((b) => b.id == filters.batch_id)?.title}
                                                                <i
                                                                    className="ri-close-line ms-2 cursor-pointer"
                                                                    onClick={() => removeFilter("batch_id")}
                                                                ></i>
                                                            </span>
                                                        )}

                                                        {selectedLabels && (
                                                            <span className="badge rounded-pill bg-secondary-subtle text-dark px-3 py-2">
                                                                Duplicate By: {selectedLabels}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <button
                                                        title="Open Filters"
                                                        className="btn btn-light border rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                        data-bs-toggle="offcanvas"
                                                        data-bs-target="#filterCanvas"
                                                        onClick={handleOpenOffcanvas}
                                                        style={{ width: 40, height: 40 }}
                                                    >
                                                        <i className="ri-filter-3-line text-primary"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                                                    <div>
                                                        <h4 className="mb-1">Duplicate Students</h4>
                                                        <small className="text-muted">
                                                            Students grouped by matching :- {selectedLabels || "Mobile"}
                                                        </small>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="bg-label-warning px-3 py-2 rounded" style={{ minWidth: "130px" }}>
                                                            <small className="d-block text-muted">Duplicate Groups</small>
                                                            <strong>{totalDuplicateGroups}</strong>
                                                        </div>

                                                        <div style={{ width: "280px" }}>
                                                            <div className="input-group">
                                                                <span className="input-group-text">
                                                                    <i className="ri-search-line"></i>
                                                                </span>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search student..."
                                                                    value={filters?.search || ""}
                                                                    onChange={(e) =>
                                                                        setFilters({
                                                                            ...filters,
                                                                            search: e.target.value.toUpperCase(),
                                                                            page: 1,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="table-responsive">
                                                    <table className="table table-hover align-middle">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th width="60">#</th>
                                                                <th width="80">Group</th>
                                                                <th>Student</th>
                                                                <th>Parents</th>
                                                                <th>Mobile</th>
                                                                <th>DOB</th>
                                                                <th>Batch / Course</th>
                                                                <th>Admission</th>
                                                                <th width="120">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="text-capitalize">
                                                            {unifiedGroups.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan="9">
                                                                        <div className="text-center py-5">
                                                                            <i className="ri-user-search-line text-muted" style={{ fontSize: "50px" }}></i>
                                                                            <h6 className="mt-3">No Duplicate Students Found</h6>
                                                                            <p className="text-muted mb-0">
                                                                                No students share the same {selectedLabels || "Mobile"} in current filters.
                                                                            </p>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                unifiedGroups.map((group, gIndex) =>
                                                                    group.items.map((item, index) => (
                                                                        <tr
                                                                            key={item.id}
                                                                            className="cursor-pointer"
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={() => navigate(`/student/details?id=${item.id}`)}
                                                                        >
                                                                            <td>{index === 0 ? gIndex + 1 : ""}</td>
                                                                            <td>
                                                                                <span className="badge rounded-pill bg-label-secondary">
                                                                                    Group {gIndex + 1}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <div>
                                                                                    <div className="fw-semibold text-primary">{item.name}</div>
                                                                                    <small className="text-muted">{item.registration_no}</small>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div>
                                                                                    <div>{item.father_name || "-"}</div>
                                                                                    <small className="text-muted">{item.mother_name || "-"}</small>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <span className="badge rounded-pill bg-label-info">{item.phone}</span>
                                                                            </td>
                                                                            <td>{formatToDate(item.dob)}</td>
                                                                            <td>
                                                                                <span className="badge rounded-pill bg-label-primary">
                                                                                    {item?.course_and_fee_info?.batch?.title || "-"} (
                                                                                    {item?.course_and_fee_info?.course?.title || "-"})
                                                                                </span>
                                                                            </td>
                                                                            <td>{formatToDate(item?.admission_date)}</td>
                                                                            <td onClick={(e) => e.stopPropagation()}>
                                                                                <div className="d-flex gap-1">
                                                                                    {hasAccess("/student/update") && (
                                                                                        <Link
                                                                                            to={`/student/update?id=${item.id}`}
                                                                                            className="btn btn-sm btn-outline-primary"
                                                                                        >
                                                                                            <i className="ri-edit-line"></i>
                                                                                        </Link>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )
                                                            )}
                                                        </tbody>
                                                    </table>
                                                    <Pagination data={data} setFilters={setFilters} filters={filters} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── FILTER OFFCANVAS ────────────────────────────────────────────── */}
            <div className="offcanvas offcanvas-end" tabIndex="-1" id="filterCanvas">
                <div className="offcanvas-header border-bottom">
                    <h5 className="offcanvas-title fw-semibold">
                        <i className="ri-filter-3-line me-2 text-primary"></i>Filters
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas"></button>
                </div>

                <div className="offcanvas-body">
                    <div className="row">


                        <div className="col-12 mb-3">
                            <label className="form-label text-muted small fw-semibold mb-2">
                                Duplicate Check By
                            </label>
                            <div className="d-flex flex-wrap gap-2">
                                {[
                                    { key: "registration_no", label: "Registration No" },
                                    { key: "name", label: "Student Name" },
                                    { key: "phone", label: "Mobile" },
                                    { key: "father_name", label: "Father Name" },
                                    { key: "mother_name", label: "Mother Name" },
                                    { key: "dob", label: "Date Of Birth" },
                                    { key: "aadhar_no", label: "Aadhar No" },
                                    { key: "jan_aadhar", label: "Jan Aadhar" },
                                    // { key: "secondary_mobile", label: "Secondary Mobile" },
                                    // { key: "father_mobile", label: "Father Mobile" },
                                    // { key: "mother_mobile", label: "Mother Mobile" },
                                ].map(({ key, label }) => {
                                    const isSelected = (tempFilters.duplicate_by || []).includes(key);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className={`btn btn-sm rounded-pill px-3 ${isSelected ? "btn-primary" : "btn-outline-primary"
                                                }`}
                                            onClick={() => {
                                                let values = [...(tempFilters.duplicate_by || [])];
                                                if (isSelected) {
                                                    values = values.filter((v) => v !== key);
                                                } else {
                                                    values.push(key);
                                                }
                                                setTempFilters({ ...tempFilters, duplicate_by: values });
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>


                        {/* <div className="col-12 mb-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Student Name/Mobile/Registration"
                                    value={tempFilters?.search || ""}
                                    onChange={(e) =>
                                        setTempFilters({ ...tempFilters, search: e.target.value.toUpperCase() })
                                    }
                                />
                                <label>Name/Mobile/Reg. No</label>
                            </div>
                        </div> */}


                        <div className="col-12 mb-3">
                            <div className="form-floating form-floating-outline">
                                <select
                                    className="form-select"
                                    value={tempFilters?.session_id || ""}
                                    onChange={(e) => setTempFilters({ ...tempFilters, session_id: e.target.value })}
                                >
                                    {sessions.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.session}:{Number(item.status) === 1 ? "Active" : "Inactive"}
                                        </option>
                                    ))}
                                </select>
                                <label>Filter Session</label>
                            </div>
                        </div>

                        <div className="col-12 mb-3">
                            <div className="form-floating form-floating-outline">
                                <select
                                    className="form-select"
                                    value={tempFilters?.course_id || ""}
                                    onChange={(e) =>
                                        setTempFilters({ ...tempFilters, course_id: e.target.value, batch_id: "" })
                                    }
                                >
                                    <option value="">All Courses / Classes</option>
                                    {courses?.map((course, index) => (
                                        <option key={index} value={course.id}>
                                            {course.title}
                                        </option>
                                    ))}
                                </select>
                                <label>Course</label>
                            </div>
                        </div>

                        <div className="col-12 mb-3">
                            <div className="form-floating form-floating-outline">
                                <select
                                    className="form-select"
                                    value={tempFilters?.batch_id || ""}
                                    onChange={(e) => setTempFilters({ ...tempFilters, batch_id: e.target.value })}
                                >
                                    <option value="">Select Batch</option>
                                    {batches?.map((batch, i) => {
                                        const courseTitle = batch?.course?.title || "";
                                        const shortCourseTitle =
                                            courseTitle.length > 20 ? courseTitle.substring(0, 20) + "..." : courseTitle;
                                        return (
                                            <option key={i} value={batch?.id}>
                                                {`${batch?.title} (${shortCourseTitle})`}
                                            </option>
                                        );
                                    })}
                                </select>
                                <label>Select Batch</label>
                            </div>
                        </div>





                    </div>

                    <div className="row g-2 mt-1">
                        <div className="col-6">
                            <button className="btn btn-primary w-100" data-bs-dismiss="offcanvas" onClick={handleApply}>
                                <i className="ri-check-line me-1"></i>Apply
                            </button>
                        </div>
                        <div className="col-6">
                            <button className="btn btn-outline-danger w-100" onClick={handleReset}>
                                <i className="ri-refresh-line me-1"></i>Reset
                            </button>
                        </div>


                    </div>
                </div>
            </div>
        </>
    );
}